import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { DynamicTitle } from '../DynamicTitle';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';
import { useAuth } from '../../lib/contexts/AuthContext';
import {
  listPropuestasFavoritasPorUsuario,
  type PropuestaFavoritaResumen,
} from '../../lib/services/propuestas-favoritas';
import { PropuestaFavoritoButton } from './PropuestaFavoritoButton';

export function ComensalLikesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<PropuestaFavoritaResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const rows = await listPropuestasFavoritasPorUsuario(user.id);
      setItems(rows);
    } catch (err) {
      console.error('Error cargando favoritos:', err);
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={() => {
            setRefreshing(true);
            void load();
          }}
          tintColor="#A20000"
        />
      }
    >
      <DynamicTitle title="Favoritos — As Gastronómico" />
      <Text style={styles.title}>Favoritos</Text>
      <Text style={styles.sub}>Propuestas que marcaste con el corazón en el catálogo.</Text>

      {loading ? (
        <ActivityIndicator color="#A20000" style={{ marginTop: 24 }} />
      ) : !user?.id ? (
        <Text style={styles.empty}>Inicia sesión para ver tus favoritos.</Text>
      ) : items.length === 0 ? (
        <Text style={styles.empty}>
          Aún no tienes favoritos. Abre una edición y toca el corazón en una propuesta.
        </Text>
      ) : (
        items.map((fav) => (
          <View key={fav.id} style={styles.card}>
            <View style={styles.cardBody}>
              <Text style={styles.cardTitulo} numberOfLines={2}>
                {fav.titulo}
              </Text>
              <Text style={styles.cardMeta} numberOfLines={1}>
                {[fav.restauranteNombre, fav.edicionNombre].filter(Boolean).join(' · ') || '—'}
              </Text>
            </View>
            <PropuestaFavoritoButton
              propuestaId={fav.propuestaId}
              favorita
              tone="onLight"
              onChange={(next) => {
                if (!next) setItems((prev) => prev.filter((x) => x.id !== fav.id));
              }}
            />
          </View>
        ))
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 32 + COMENSAL_TAB_BAR_OVERLAY_INSET,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#030213',
    marginBottom: 8,
  },
  sub: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
    marginBottom: 20,
  },
  empty: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginTop: 8,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardTitulo: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  cardMeta: {
    marginTop: 3,
    fontSize: 12,
    color: '#6b7280',
  },
});
